import { ReactNode } from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { AnimatedNumber } from './AnimatedNumber';
import { Sparkline } from './Sparkline';
import { cn } from '@/lib/utils';

interface Props {
  label: string;
  value: number;
  formatter?: (n: number) => string;
  change?: number;       // zmiana % vs poprzedni okres (np. +12.5)
  sparkData?: number[];  // 7-30 punktów trendu
  icon?: ReactNode;
  color?: string;
  className?: string;
}

/**
 * Karta KPI z:
 *  - animowaną wartością (counter)
 *  - sparkline trendem
 *  - wskaźnikiem zmiany vs poprzedni okres (zielony/czerwony/szary)
 *  - ikoną
 */
export function KpiCard({ label, value, formatter, change, sparkData, icon, color = '#2E5BFF', className }: Props) {
  const changeType = change && change > 0 ? 'up' : change && change < 0 ? 'down' : 'flat';

  return (
    <div className={cn('card p-5 group hover:shadow-md transition-shadow', className)}>
      <div className="flex items-start justify-between mb-3">
        <p className="text-xs font-medium uppercase tracking-wider text-slate-500">{label}</p>
        {icon && (
          <div
            className="h-8 w-8 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: `${color}15` }}
          >
            <span style={{ color }}>{icon}</span>
          </div>
        )}
      </div>

      <div className="flex items-end justify-between">
        <div>
          <p className="text-2xl font-semibold">
            <AnimatedNumber value={value} formatter={formatter} />
          </p>
          {change !== undefined && (
            <div className={cn(
              'flex items-center gap-1 mt-1 text-xs font-medium',
              changeType === 'up' && 'text-emerald-600',
              changeType === 'down' && 'text-red-500',
              changeType === 'flat' && 'text-slate-400',
            )}>
              {changeType === 'up' && <TrendingUp size={12} />}
              {changeType === 'down' && <TrendingDown size={12} />}
              {changeType === 'flat' && <Minus size={12} />}
              <span>
                {change > 0 ? '+' : ''}{change?.toFixed(1)}% vs prev
              </span>
            </div>
          )}
        </div>

        {sparkData && sparkData.length > 2 && (
          <Sparkline
            data={sparkData}
            width={80}
            height={32}
            color={changeType === 'down' ? '#EF4444' : color}
            fillColor={changeType === 'down' ? '#EF4444' : color}
            className="opacity-80 group-hover:opacity-100 transition-opacity"
          />
        )}
      </div>
    </div>
  );
}
